import React from "react";
import { Col, Row } from "react-bootstrap";
import { object, string, number, oneOfType, bool } from "prop-types";
import { traitAbbrevToTestFieldObj } from "utils/helperConstants";
import NumberedScoreScale from "./NumberedScoreScale";
import ScoreScaleWithPoles from "./ScoreScaleWithPoles";
import { scaleTraitFieldToPretty } from "./constants";

const createLabeledScoreScale = (
  label,
  score,
  max,
  hasScoreRange,
  scoreRangeMin,
  scoreRangeMax
) => (
  <Row key={label} className="pt-3">
    <Col lg={4} className="pl-0 pr-2 mr-2">
      <div className="text-right" style={{ color: "#011f4e" }}>
        {label}
      </div>
    </Col>
    <NumberedScoreScale
      cellSpan={max / 5}
      hasScoreRange={hasScoreRange}
      maxScore={max}
      score={score}
      scoreRangeMax={Number(scoreRangeMax)}
      scoreRangeMin={Number(scoreRangeMin)}
    />
  </Row>
);

const createScoreScaleWithPoles = (
  traitLabel,
  score,
  max,
  lowPoleLabel,
  highPoleLabel,
  scoreRangeLimits
) => (
  <Row key={traitLabel} style={{ height: "70px" }}>
    <Col lg={12} className="d-flex justify-content-start">
      <h5 style={{ color: "#19355f", fontSize: "13px" }}>{traitLabel}</h5>
    </Col>
    <Col
      lg={2}
      style={{ fontSize: "11px" }}
      className="d-flex justify-content-end text-right"
      dangerouslySetInnerHTML={{ __html: lowPoleLabel }}
    />
    <ScoreScaleWithPoles
      score={score}
      maxScore={max}
      cellSpan={20}
      scoreRangeLimits={scoreRangeLimits}
    />
    <Col
      lg={2}
      style={{ fontSize: "11px" }}
      dangerouslySetInnerHTML={{ __html: highPoleLabel }}
    />
  </Row>
);

const MultipleScoreScales = ({
  testId,
  scores,
  maxScore,
  scoreRange,
  scoreRangeType,
  fromEPPReport,
  traits,
}) => {
  if ([10, 12, 13, 14, 23, 31, 37, 42].includes(testId)) {
    const labeledScoreScales = [];

    Object.entries(scaleTraitFieldToPretty[testId]).forEach(
      ([traitName, prettyTraitName]) => {
        if (
          scoreRange &&
          traitName === traitAbbrevToTestFieldObj[testId][scoreRangeType] &&
          ![13, 31].includes(testId) // hide score ranges for CPI and WPP to be consistent with platform HTML/PDF
        ) {
          labeledScoreScales.push(
            createLabeledScoreScale(
              prettyTraitName,
              scores.get(traitName),
              maxScore,
              true,
              scoreRange.get("min_scoreRange"),
              scoreRange.get("max_scoreRange")
            )
          );
        } else {
          labeledScoreScales.push(
            createLabeledScoreScale(
              prettyTraitName,
              scores.get(traitName),
              maxScore
            )
          );
        }
      }
    );

    return (
      <Col lg={11} className="px-2">
        {labeledScoreScales}
      </Col>
    );
  }

  if (testId === 34) {
    if (fromEPPReport) {
      const scoreScalesWithPoles = [];

      traits
        .keySeq()
        .sort((a, b) => a.localeCompare(b))
        .forEach((trait) => {
          const traitDetails = traits.get(trait);

          scoreScalesWithPoles.push(
            createScoreScaleWithPoles(
              traitDetails.get("name"),
              scores.get(`${trait}_adjPercentile`),
              maxScore,
              traitDetails.get("low"),
              traitDetails.get("high"),
              scoreRange ? scoreRange.defaultTraitRanges.get(trait) : null
            )
          );
        });

      return <Col>{scoreScalesWithPoles}</Col>;
    }

    if (scoreRange) {
      const scoreRangeTraitScales = [];

      scoreRange.keySeq().forEach((trait) => {
        scoreRangeTraitScales.push(
          createLabeledScoreScale(
            scaleTraitFieldToPretty[testId][`${trait}_adjPercentile`],
            scores.get(`${trait}_adjPercentile`),
            maxScore,
            true,
            scoreRange.get(trait).get("low"),
            scoreRange.get(trait).get("high")
          )
        );
      });

      return (
        <Col lg={11} className="px-0" style={{ marginLeft: "-10px" }}>
          {scoreRangeTraitScales}
        </Col>
      );
    }

    const firstColumnTraitScales = [];
    const secondColumnTraitScales = [];

    scores
      .filter((score, scoreName) => scoreName.includes("adjPercentile"))
      .keySeq()
      .forEach((scoreName) => {
        if (
          scaleTraitFieldToPretty[testId][scoreName] &&
          scores.get(scoreName)
        ) {
          if (firstColumnTraitScales.length <= 5) {
            firstColumnTraitScales.push(
              createLabeledScoreScale(
                scaleTraitFieldToPretty[testId][scoreName],
                scores.get(scoreName),
                maxScore
              )
            );
          } else {
            secondColumnTraitScales.push(
              createLabeledScoreScale(
                scaleTraitFieldToPretty[testId][scoreName],
                scores.get(scoreName),
                maxScore
              )
            );
          }
        }
      });

    return (
      <>
        <Col style={{ marginLeft: "-20px" }}>{firstColumnTraitScales}</Col>
        <Col>{secondColumnTraitScales}</Col>
      </>
    );
  }

  if (testId === 48) {
    const competencyScales = [];

    scores.forEach((score, competencyName) => {
      competencyScales.push(
        // eslint-disable-next-line react/no-array-index-key
        <Col lg={6} className="competency-score-column" key={competencyName}>
          {createLabeledScoreScale(competencyName, score, maxScore)}
        </Col>
      );
    });

    return competencyScales;
  }

  return null;
};

MultipleScoreScales.propTypes = {
  testId: number,
  scores: object,
  maxScore: oneOfType([string, number]),
  scoreRange: oneOfType([object, bool]),
  scoreRangeType: oneOfType([string, bool]),
  fromEPPReport: bool,
  traits: object,
};

export default MultipleScoreScales;
